// pages/carbon-credits.js
import { CarbonCreditsHtml } from "../../components/skins/carbon-credits.ts";
import { globalState } from "../../framework/globalState.ts";
import { subscribeRegister } from "../../services/mqttClient.ts";
import { RechartsChartManager } from "../../components/charts/RechartsChartManager.tsx";
import { chartStyles } from "../../components/charts/ChartStyles.ts";
import { FullscreenToggle } from "../../components/utils/fullscreen-toggle.ts";
import {
  getCarbonWarmSnapshot,
  primeCarbonWarmSnapshot,
  startCarbonWarmPolling,
  subscribeCarbonWarmSnapshot,
} from "../../services/carbonWarmCache.ts";

export default function CarbonCreditsPage() {
  const container = document.createElement("div");
  container.classList.add("parent-container", "carbon-credits-page");
  container.innerHTML = new CarbonCreditsHtml().render();

  const siteWithData = globalState.currentSiteWithData;
  if (!siteWithData) return container;

  const { site, devices } = siteWithData;
  const registers = devices.flatMap(d =>
    (d.registers || []).map(r => ({ ...r, deviceName: d.name, deviceId: d._id }))
  );

  const subscriptions = [];
  const chartManager = new RechartsChartManager(site, registers);
  let carbonWarmUnsubscribe: (() => void) | null = null;

  // -------------------------
  // Live Carbon Data
  // -------------------------
  const instantNode = container.querySelector('[data-carbon="instant_kgco2"]');
  const dailyNode = container.querySelector('[data-carbon="daily_tco2"]');
  const monthlyNode = container.querySelector('[data-carbon="monthly_tco2"]');
  const eligibleNode = container.querySelector('[data-carbon="eligible_credits"]');

  const co2Register = registers.find(r => r.name === "CO₂ Emissions Reduced_1");
  if (co2Register) {
    const unsubscribe = subscribeRegister(site._id, co2Register.deviceId, co2Register._id, payload => {
      const value = typeof payload?.value === "number" ? payload.value : null;
      const scaled = (value * 1e-12).toFixed(3);
      if (instantNode) instantNode.textContent = scaled;
      chartManager.updateChart("co2TrendChart", scaled);
    });
    subscriptions.push(unsubscribe);
  }

  // -------------------------
  // Carbon Summary Load
  // -------------------------
  const applyCarbonSummary = (summary: any) => {
    if (!summary) return;

    const { today, monthlyBreakdown, yearlyBreakdown } = summary;

    if (dailyNode) dailyNode.textContent = Number(today || 0).toFixed(3);

    const currentMonth = new Date().toLocaleString("en-US", { month: "long" });
    const monthObj = Array.isArray(monthlyBreakdown)
      ? monthlyBreakdown.find((m: any) => m.month === currentMonth)
      : null;
    if (monthlyNode) monthlyNode.textContent = Number(monthObj?.tonnesReduced || 0).toFixed(3);

    const eligible = Array.isArray(yearlyBreakdown)
      ? yearlyBreakdown.reduce((acc: number, year: any) => acc + Number(year?.tonnesReduced || 0), 0)
      : 0;
    if (eligibleNode) eligibleNode.textContent = Math.abs(eligible).toFixed(3);
  };

  const cachedCarbonSummary = getCarbonWarmSnapshot(site._id);
  if (cachedCarbonSummary?.summary) {
    applyCarbonSummary(cachedCarbonSummary.summary);
  } else {
    void primeCarbonWarmSnapshot(site._id).then((snapshot) => {
      applyCarbonSummary(snapshot.summary);
    }).catch((err) => {
      console.error("Carbon summary load failed:", err);
    });
  }

  startCarbonWarmPolling(site._id, 5 * 60 * 1000);
  carbonWarmUnsubscribe = subscribeCarbonWarmSnapshot(site._id, (snapshot) => {
    applyCarbonSummary(snapshot.summary);
  });

  // -------------------------
  // Charts
  // -------------------------
  chartManager.createChart("co2TrendChart", [], { ...chartStyles.co2TrendLayout, showControls: true }, container);

  // -------------------------
  // Fullscreen
  // -------------------------
  requestAnimationFrame(() => new FullscreenToggle().init());

  // -------------------------
  // Cleanup
  // -------------------------
  container.cleanup = () => {
    try {
      if (typeof carbonWarmUnsubscribe === 'function') {
        carbonWarmUnsubscribe();
        carbonWarmUnsubscribe = null;
      }
    } catch { /* ignore */ }
    try {
      subscriptions.forEach(fn => typeof fn === "function" && fn());
    } catch { /* ignore */ }
    try {
      chartManager.cleanup();
    } catch { /* ignore */ }
  };

  return container;
}
